import {
  Ban,
  CircleAlert,
  CircleCheck,
  CircleX,
  Clock,
  Loader,
  type LucideIcon
} from 'lucide-react';

import type { BatchJobSummary } from '@/hooks/useWorkflowBatchJobs';

export type BatchJobVisualStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'done'
  | 'partial'
  | 'cancelled'
  | 'failed';

export function visualStatusOf(
  job: Pick<BatchJobSummary, 'status' | 'failed'>
): BatchJobVisualStatus {
  if (job.status === 'PENDING') {
    return 'pending';
  }
  if (job.status === 'QUEUED') {
    return 'queued';
  }
  if (job.status === 'CANCELLED') {
    return 'cancelled';
  }
  if (job.status === 'FAILED') {
    return 'failed';
  }
  if (job.status === 'DONE') {
    return job.failed > 0 ? 'partial' : 'done';
  }
  return 'running';
}

export const BATCH_JOB_STATUS_META = {
  pending: {
    label: 'Preparing',
    rail: 'bg-primary',
    pill: 'bg-primary/15 text-primary',
    chip: 'bg-primary/15 text-primary',
    Icon: Clock
  },
  queued: {
    label: 'Queued',
    rail: 'bg-primary',
    pill: 'bg-primary/15 text-primary',
    chip: 'bg-primary/15 text-primary',
    Icon: Clock
  },
  running: {
    label: 'Running',
    rail: 'bg-primary',
    pill: 'bg-primary/15 text-primary',
    chip: 'bg-primary/15 text-primary',
    Icon: Loader
  },
  done: {
    label: 'Completed',
    rail: 'bg-green-600',
    pill: 'bg-green-600/15 text-green-600',
    chip: 'bg-green-600/15 text-green-600',
    Icon: CircleCheck
  },
  partial: {
    label: 'Completed with failures',
    rail: 'bg-destructive',
    pill: 'bg-destructive/15 text-destructive',
    chip: 'bg-destructive/15 text-destructive',
    Icon: CircleAlert
  },
  cancelled: {
    label: 'Cancelled',
    rail: 'bg-foreground/25',
    pill: 'bg-muted text-foreground/50',
    chip: 'bg-muted text-foreground/50',
    Icon: Ban
  },
  failed: {
    label: 'Failed',
    rail: 'bg-destructive',
    pill: 'bg-destructive/15 text-destructive',
    chip: 'bg-destructive/15 text-destructive',
    Icon: CircleX
  }
} satisfies Record<
  BatchJobVisualStatus,
  { label: string; rail: string; pill: string; chip: string; Icon: LucideIcon }
>;
